import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Headphones, Square, Loader2 } from "lucide-react";
import { createSpeaker, getVoice, fetchTtsConfig } from "../lib/voice";
import { useLang, LANGS } from "../lib/i18n";
import { VoiceSetting } from "./AssistantWidget";

// Small badge telling the listener whether a native-accent voice is used for the current site language.
export const NativeVoiceBadge = () => {
  const { lang } = useLang();
  const [cfg, setCfg] = useState(null);
  useEffect(() => { fetchTtsConfig().then(setCfg); }, []);
  if (lang === "en" || !cfg) return null;
  const name = LANGS.find(([k]) => k === lang)?.[1] || lang;
  const native = cfg.native_languages?.includes(lang);
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${native ? "bg-green-100 text-green-800" : "bg-black/5 text-[#0a0d12]/60"}`} title={native ? `Read with a native ${name} accent` : `${name} audio uses a standard voice until native voices are enabled`} data-testid="native-voice-badge">
      {native ? `Native ${name} voice` : `${name} · standard voice`}
    </span>
  );
};

// "Listen to this page": reads the main content aloud for low-literacy / low-vision visitors. Hidden on admin/portal routes.
export default function ListenToPage() {
  const [state, setState] = useState({ playing: false, loading: false });
  const speaker = useRef(createSpeaker());
  const loc = useLocation();
  const hidden = /^\/(admin|donor-portal|beneficiary-portal|volunteer\/portal|partner\/track)/.test(loc.pathname);

  useEffect(() => { speaker.current.stop(); setState({ playing: false, loading: false }); }, [loc.pathname]);
  useEffect(() => () => speaker.current.stop(), []);
  if (hidden) return null;

  const start = async () => {
    const main = document.getElementById("main");
    if (!main) return;
    const clone = main.cloneNode(true);
    clone.querySelectorAll("script, style, nav, form, button, input, select, textarea, [aria-hidden='true'], .sr-only, [data-no-read]").forEach((n) => n.remove());
    const text = (clone.innerText || "").replace(/\s+/g, " ").trim().slice(0, 14000);
    if (!text) return;
    try { await speaker.current.speak(text, getVoice(), setState); } catch { setState({ playing: false, loading: false, error: true }); }
  };
  const active = state.playing || state.loading;

  return (
    <div className="flex items-center justify-end gap-2 px-4 sm:px-6 lg:px-8 py-1.5 bg-[#f8f4e8] border-b border-black/5 text-[11px]" data-testid="listen-bar" data-no-read>
      <span className="text-[#0a0d12]/60 hidden sm:inline">Accessibility:</span>
      <VoiceSetting />
      <NativeVoiceBadge />
      <button type="button" onClick={() => (active ? (speaker.current.stop(), setState({ playing: false, loading: false })) : start())} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold min-h-[32px] ${active ? "bg-red-700 text-white" : "bg-[#155e3f] text-white"}`} aria-pressed={active} data-testid="listen-toggle">
        {state.loading ? <Loader2 size={14} className="animate-spin" /> : active ? <Square size={14} /> : <Headphones size={14} />}
        {active ? `Stop${state.total > 1 ? ` (${(state.index || 0) + 1}/${state.total})` : ""}` : "Listen to this page"}
      </button>
    </div>
  );
}
